import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { API_URL } from '@http';
import { useSettingsStore } from '@hooks/useStores';
import { SunIcon, MoonIcon } from '@components/common/ui/icons';
import * as styles from '@pages/LandingPage.module.css';

interface PublicNote {
    id: string;
    title: string;
    meta?: {
        excerpt?: string;
    };
    ownerName?: string;
    ownerId?: string;
    updatedAt?: string;
}

export const LandingPage: React.FC = observer(() => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const settingsStore = useSettingsStore();
    const [notes, setNotes] = useState<PublicNote[]>([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const notesRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const loadPublicNotes = async () => {
            try {
                const res = await fetch(`${API_URL}/notes/public`);
                if (res.ok) {
                    const data = await res.json();
                    setNotes(Array.isArray(data) ? data : []);
                }
            } catch {
                // Не критично
            } finally {
                setLoading(false);
            }
        };
        loadPublicNotes();
    }, []);

    const filtered = search.trim()
        ? notes.filter(
              (n) =>
                  n.title?.toLowerCase().includes(search.toLowerCase()) ||
                  n.meta?.excerpt?.toLowerCase().includes(search.toLowerCase()),
          )
        : notes;

    const formatDate = (dateStr?: string) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return d.toLocaleDateString(settingsStore.language === 'ru' ? 'ru-RU' : 'en-US', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        });
    };

    const scrollToNotes = () => {
        notesRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <div className={styles.landing}>
            {/* Theme toggle */}
            <button
                className={styles.themeToggle}
                onClick={() => settingsStore.toggleTheme()}
                title={settingsStore.theme === 'dark' ? t('landing.themeLight') : t('landing.themeDark')}
            >
                {settingsStore.theme === 'dark' ? <SunIcon /> : <MoonIcon />}
            </button>

            {/* Header */}
            <header className={styles.header}>
                <div className={styles.logo}>
                    <div className={styles.logoIcon}>N</div>
                    <span className={styles.logoText}>Note Editor</span>
                </div>
                <div className={styles.headerActions}>
                    <button className={styles.loginBtn} onClick={() => navigate('/login')}>
                        {t('landing.login')}
                    </button>
                    <button className={styles.registerBtn} onClick={() => navigate('/login')}>
                        {t('landing.register')}
                    </button>
                </div>
            </header>

            {/* Hero */}
            <section className={styles.hero}>
                <h1 className={styles.heroTitle}>{t('landing.heroTitle')}</h1>
                <p className={styles.heroDescription}>{t('landing.heroDescription')}</p>
                <div className={styles.heroActions}>
                    <button className={styles.ctaBtn} onClick={() => navigate('/login')}>
                        {t('landing.startFree')}
                    </button>
                    {notes.length > 0 && (
                        <button className={styles.secondaryBtn} onClick={scrollToNotes}>
                            {t('landing.publicNotes')}
                        </button>
                    )}
                </div>
            </section>

            {/* Features */}
            <div className={styles.features}>
                <div className={styles.featureCard}>
                    <div className={styles.featureIcon}>&#9998;</div>
                    <h3 className={styles.featureTitle}>{t('landing.featureEditorTitle')}</h3>
                    <p className={styles.featureDescription}>{t('landing.featureEditorDescription')}</p>
                </div>
                <div className={styles.featureCard}>
                    <div className={styles.featureIcon}>&#8644;</div>
                    <h3 className={styles.featureTitle}>{t('landing.featureCollabTitle')}</h3>
                    <p className={styles.featureDescription}>{t('landing.featureCollabDescription')}</p>
                </div>
                <div className={styles.featureCard}>
                    <div className={styles.featureIcon}>&#128274;</div>
                    <h3 className={styles.featureTitle}>{t('landing.featureAccessTitle')}</h3>
                    <p className={styles.featureDescription}>{t('landing.featureAccessDescription')}</p>
                </div>
            </div>

            {/* Public notes */}
            <section className={styles.publicSection} ref={notesRef}>
                <div className={styles.sectionLabel}>
                    <span className={styles.sectionLabelText}>{t('landing.sectionTitle')}</span>
                    <div className={styles.sectionLabelLine} />
                </div>

                <div className={styles.sectionHeader}>
                    <h2 className={styles.sectionTitle}>
                        {loading ? t('landing.loading') : t('landing.notesCount', { count: notes.length })}
                    </h2>
                    {notes.length > 0 && (
                        <input
                            className={styles.searchInput}
                            type="text"
                            placeholder={t('landing.searchPlaceholder')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    )}
                </div>

                {loading ? (
                    <div className={styles.loadingNotes}>{t('landing.loading')}</div>
                ) : filtered.length === 0 ? (
                    <div className={styles.emptyNotes}>
                        {search ? t('landing.emptySearch') : t('landing.emptyNotes')}
                    </div>
                ) : (
                    <div className={styles.notesGrid}>
                        {filtered.map((note) => (
                            <a
                                key={note.id}
                                className={styles.noteCard}
                                href={`/note/${note.id}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    navigate(`/note/${note.id}`);
                                }}
                            >
                                <h3 className={styles.noteTitle}>{note.title || t('landing.untitled')}</h3>
                                {note.meta?.excerpt && (
                                    <p className={styles.noteExcerpt}>{note.meta.excerpt}</p>
                                )}
                                <div className={styles.noteMeta}>
                                    {note.ownerName && (
                                        <span className={styles.noteAuthor}>{note.ownerName}</span>
                                    )}
                                    {note.ownerName && formatDate(note.updatedAt) && (
                                        <span>·</span>
                                    )}
                                    {formatDate(note.updatedAt)}
                                </div>
                            </a>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
});
